import { useCallback, useEffect, useState, type CSSProperties } from "react"
import { AlertTriangle, X } from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import {
  type FailedSend,
  acknowledgeAllFailedSends,
  acknowledgeFailedSend,
  explainSendError,
  fetchFailedSends,
  sendKindLabel,
} from "@/lib/undoSend"

/**
 * Globaler Alarm fuer gescheiterte E-Mail-Sends (06.08.2026).
 *
 * Vorher: Der Soft-Send-Toast verschwand nach 30s. Scheiterte der SMTP-Versand
 * danach, stand das nur als `pending_sends.status='failed'` in der Datenbank —
 * der Nutzer suchte die Mail vergeblich im Gesendet-Ordner (Vorfall: Sends 6, 7
 * und 8, Auth-Fehler bei M365 bzw. Gmail-App-Passwort).
 *
 * Jetzt: Diese Karte laedt beim Start und danach alle 60s die unquittierten
 * Fehler der letzten 24h und zeigt Empfaenger, Betreff und die error_message
 * aus der DB. Sie verschwindet erst per "Verstanden" — ein verlorener Send
 * soll sich nicht wegklicken lassen, indem man kurz woanders hinschaut.
 *
 * Bewusst global (neben <UndoSendToast> in App.tsx) statt nur in der Inbox:
 * gesendet wird auch aus Review, Kalender-Notify und Weiterleitung heraus.
 */

const POLL_MS = 60_000

function fmtZeit(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString("de-DE", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  })
}

export function FailedSendAlert() {
  const [items, setItems] = useState<FailedSend[]>([])
  const [collapsed, setCollapsed] = useState(false)
  // Auf /login gibt es keine Session — Polling wuerde nur 401er produzieren.
  const eingeloggt = useLocation().pathname !== "/login"

  const laden = useCallback(async () => {
    setItems(await fetchFailedSends(24))
  }, [])

  useEffect(() => {
    if (!eingeloggt) return
    void laden()
    const timer = setInterval(() => void laden(), POLL_MS)
    // Nach Tab-Wechsel sofort nachladen statt bis zum naechsten Tick zu warten.
    const onFocus = () => void laden()
    window.addEventListener("focus", onFocus)
    return () => {
      clearInterval(timer)
      window.removeEventListener("focus", onFocus)
    }
  }, [laden, eingeloggt])

  const quittieren = async (id: number) => {
    // Am 29.10.2026 38 s nach dem Fehler weggeklickt — die Antwort blieb
    // ungesendet. Deshalb einmal nachfragen.
    if (!window.confirm(
      "Die E-Mail ist noch NICHT versendet. Hinweis trotzdem ausblenden?",
    )) return
    setItems((prev) => prev.filter((x) => x.id !== id))
    await acknowledgeFailedSend(id)
  }

  const alleQuittieren = async () => {
    if (!window.confirm(
      "Diese E-Mails sind noch NICHT versendet. Alle Hinweise trotzdem ausblenden?",
    )) return
    setItems([])
    await acknowledgeAllFailedSends(24)
  }

  if (items.length === 0) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9998,
        width: "min(680px, calc(100vw - 32px))",
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderLeft: "4px solid #dc2626",
        borderRadius: 8,
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        color: "#7f1d1d",
        fontSize: 14,
      }}
      role="alert"
    >
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 14px",
          borderBottom: collapsed ? "none" : "1px solid #fecaca",
        }}
      >
        <AlertTriangle className="w-5 h-5" style={{ flexShrink: 0, color: "#dc2626" }} />
        <div style={{ flex: 1, fontWeight: 600 }}>
          {items.length === 1
            ? "Eine E-Mail wurde NICHT versendet"
            : `${items.length} E-Mails wurden NICHT versendet`}
        </div>
        <button
          onClick={() => setCollapsed((c) => !c)}
          style={btnStyle}
        >
          {collapsed ? "Details" : "Einklappen"}
        </button>
        {items.length > 1 && (
          <button onClick={() => void alleQuittieren()} style={btnStyle}>
            Alle verstanden
          </button>
        )}
      </div>

      {!collapsed && (
        <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
          {items.map((it) => {
            const hinweis = explainSendError(it.error_message)
            return (
              <div
                key={it.id}
                style={{
                  padding: "10px 14px",
                  borderBottom: "1px solid #fee2e2",
                  display: "flex", gap: 10, alignItems: "flex-start",
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>
                    {sendKindLabel(it.kind)}
                    {it.to ? ` an ${it.to}` : ""}
                  </div>
                  {it.subject && (
                    <div style={{ fontSize: 13, opacity: 0.9 }}>
                      Betreff: {it.subject}
                    </div>
                  )}
                  {hinweis && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>{hinweis}</div>
                  )}
                  {it.kind === "reply" && it.email_id ? (
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <Link
                        to={`/emails/${it.email_id}`}
                        style={{
                          background: "#dc2626", color: "#fff", borderRadius: 6,
                          padding: "6px 12px", fontSize: 13, fontWeight: 600,
                          textDecoration: "none",
                        }}
                      >
                        E-Mail öffnen und neu senden
                      </Link>
                      <span style={{ fontSize: 12 }}>
                        Dein Text liegt dort wieder als Entwurf.
                      </span>
                    </div>
                  ) : null}
                  <div
                    style={{
                      fontSize: 12, marginTop: 4, opacity: 0.75,
                      fontFamily: "ui-monospace, monospace",
                      wordBreak: "break-word",
                    }}
                  >
                    {it.error_message || "Kein Fehlertext gespeichert"}
                  </div>
                  <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>
                    {fmtZeit(it.sent_at || it.created_at)} · {it.tenant_slug} ·{" "}
                    {it.attempts} Versuch{it.attempts === 1 ? "" : "e"}
                    {it.email_id ? (
                      <>
                        {" · "}
                        <Link
                          to={`/emails/${it.email_id}`}
                          style={{ color: "#b91c1c", textDecoration: "underline" }}
                        >
                          zur E-Mail
                        </Link>
                      </>
                    ) : null}
                  </div>
                </div>
                <button
                  onClick={() => void quittieren(it.id)}
                  style={{ ...btnStyle, flexShrink: 0 }}
                  title="Fehler quittieren — verschwindet auch aus dem Health-Check"
                >
                  <X className="w-3.5 h-3.5" style={{ display: "inline", marginRight: 4 }} />
                  Verstanden
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const btnStyle: CSSProperties = {
  background: "#fff",
  color: "#991b1b",
  border: "1px solid #fca5a5",
  borderRadius: 6,
  padding: "5px 10px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
}
